import React, { useState, useEffect } from 'react';
import { Activity, Bug, Zap, Clock, Terminal, Shield, CheckCircle, Trash2 } from 'lucide-react';
import { UserAvatar, timeAgo } from './App';

export default function PlatformTab({ ctx }) {
  const { T, iS, api, notify, isAdmin, isEN } = ctx;
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewType, setViewType] = useState('bug'); // 'bug' oder 'feature'
  const [expandedLog, setExpandedLog] = useState(null);

  const STATUSES = [
    { value: 'Neu', label: isEN ? 'New' : 'Neu', color: '#94A3B8' },
    { value: 'In Prüfung', label: isEN ? 'Under Review' : 'In Prüfung', color: '#F59E0B' },
    { value: 'Geplant', label: isEN ? 'Planned' : 'Geplant', color: '#8B5CF6' },
    { value: 'In Arbeit', label: isEN ? 'In Progress' : 'In Arbeit', color: '#60A5FA' },
    { value: 'Gefixt', label: isEN ? 'Fixed' : 'Gefixt', color: '#34D399' },
    { value: 'Umgesetzt', label: isEN ? 'Implemented' : 'Umgesetzt', color: '#34D399' },
    { value: 'Abgelehnt', label: isEN ? 'Rejected' : 'Abgelehnt', color: '#FF4757' }
  ];

  const loadTickets = async () => {
    setLoading(true);
    try {
      const data = await api('/system-tickets');
      setTickets(data);
    } catch(e) { notify("Fehler beim Laden: " + e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadTickets(); }, [api]);

  const updateStatus = async (id, newStatus) => {
    try {
      const updated = await api(`/system-tickets/${id}/status`, { method: 'PUT', body: JSON.stringify({ status: newStatus }) });
      setTickets(prev => prev.map(t => t.id === id ? { ...t, status: updated.status } : t));
      notify(isEN ? "Status updated ✅" : "Status aktualisiert ✅");
    } catch(e) { notify("Fehler: " + e.message); }
  };

  // 🚀 NEU: Die Löschen-Funktion
  const deleteTicket = async (id) => {
    if (!window.confirm(isEN ? "Delete this report permanently?" : "Diesen Bericht wirklich dauerhaft löschen?")) return;
    try {
      await api(`/system-tickets/${id}`, { method: 'DELETE' });
      setTickets(prev => prev.filter(t => t.id !== id));
      notify(isEN ? "Report deleted 🗑" : "Bericht gelöscht 🗑");
    } catch(e) { notify("Fehler: " + e.message); }
  };

  const filteredTickets = tickets.filter(t => t.type === viewType);

  if (loading) return <div style={{ color: T.textFaint, textAlign: "center", padding: 40 }}>{isEN ? "Loading..." : "Laden..."}</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24, maxWidth: 1000, margin: "0 auto", width: "100%" }}>
      
      {/* HEADER */}
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", background: T.card, padding: "24px", borderRadius: 16, border: `1px solid ${T.border}`, boxShadow: "0 4px 12px rgba(0,0,0,0.02)", gap: 16 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: T.text, display: "flex", alignItems: "center", gap: 10 }}>
            <Activity color="#0ECFD9" size={26} />
            {isEN ? "TaskFlow Status & Updates" : "TaskFlow Status & Feedback"}
          </h2>
          <p style={{ color: T.textMuted, fontSize: 13, marginTop: 6 }}>
            {isEN ? "Transparent overview of reported bugs and platform feature requests." : "Transparente Übersicht über gemeldete Fehler und geplante Plattform-Features."}
          </p>
        </div>

        {/* TAB SWITCHER */}
        <div style={{ display: "flex", background: T.input, border: `1px solid ${T.border2}`, borderRadius: 11, padding: 4 }}>
          <button onClick={() => setViewType("bug")} style={{ background: viewType === "bug" ? T.card : "transparent", color: viewType === "bug" ? "#FF4757" : T.textMuted, border: "none", padding: "8px 16px", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, boxShadow: viewType === "bug" ? "0 2px 8px rgba(0,0,0,0.05)" : "none" }}>
            <Bug size={16}/> {isEN ? "Bug Reports" : "Fehlerberichte"}
          </button>
          <button onClick={() => setViewType("feature")} style={{ background: viewType === "feature" ? T.card : "transparent", color: viewType === "feature" ? "#0ECFD9" : T.textMuted, border: "none", padding: "8px 16px", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, boxShadow: viewType === "feature" ? "0 2px 8px rgba(0,0,0,0.05)" : "none" }}>
            <Zap size={16}/> {isEN ? "Feature Requests" : "Ideen & Wünsche"}
          </button>
        </div>
      </div>

      {/* TICKET LIST */}
      {filteredTickets.length === 0 ? (
        <div style={{ padding: 60, textAlign: "center", color: T.textFaint, background: T.card, border: `1px solid ${T.border}`, borderRadius: 16 }}>
          <CheckCircle size={48} style={{ opacity: 0.5, margin: "0 auto 16px", color: "#34D399" }} />
          <div>{isEN ? "Everything looks perfect! No open reports here." : "Alles sieht perfekt aus! Keine Einträge vorhanden."}</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {filteredTickets.map(ticket => {
            const sObj = STATUSES.find(s => s.value === ticket.status) || STATUSES[0];
            const isResolved = ticket.status === 'Gefixt' || ticket.status === 'Umgesetzt';
            
            return (
              <div key={ticket.id} style={{ display: "flex", flexDirection: "column", background: T.card, border: `1px solid ${isResolved ? "rgba(52,211,153,0.3)" : T.border}`, borderRadius: 16, padding: "20px", boxShadow: "0 4px 12px rgba(0,0,0,0.02)", opacity: isResolved ? 0.7 : 1 }}>
                
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
                      {isResolved && <CheckCircle size={18} color="#34D399" />}
                      {ticket.title}
                    </div>
                    <div style={{ fontSize: 14, color: T.textMuted, lineHeight: 1.6, whiteSpace: "pre-wrap", marginBottom: 16 }}>
                      {ticket.description}
                    </div>
                    
                    {/* META INFOS (User & Date) */}
                    <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 12, color: T.textFaint }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <div style={{ width: 18, height: 18, borderRadius: 5, background: ticket.reporter_color || T.border2, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 9 }}>
                          {ticket.reporter_name ? ticket.reporter_name[0].toUpperCase() : "?"}
                        </div>
                        <span style={{ fontWeight: 600, color: T.textSub }}>{ticket.reporter_name || "System"}</span>
                      </div>
                      <span>•</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <Clock size={12} /> {timeAgo(ticket.created_at)}
                      </div>
                    </div>
                  </div>

                  {/* STATUS BADGE / ADMIN CONTROLS */}
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 10 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {isAdmin ? (
                        <select 
                          value={ticket.status || 'Neu'} 
                          onChange={e => updateStatus(ticket.id, e.target.value)}
                          style={{ background: sObj.color + "20", color: sObj.color, border: `1px solid ${sObj.color}40`, padding: "6px 12px", borderRadius: 8, fontSize: 12, fontWeight: 700, outline: "none", cursor: "pointer" }}
                        >
                          {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      ) : (
                        <span style={{ background: sObj.color + "20", color: sObj.color, padding: "6px 12px", borderRadius: 8, fontSize: 12, fontWeight: 700, border: `1px solid ${sObj.color}40` }}>
                          {sObj.label}
                        </span>
                      )}

                      {/* 🚀 NEU: DER MÜLLEIMER FÜR DEN ADMIN */}
                      {isAdmin && (
                        <button onClick={() => deleteTicket(ticket.id)} style={{ background: "transparent", border: "none", color: "#FF4757", cursor: "pointer", padding: "4px" }} className="hov" title={isEN ? "Delete Report" : "Bericht löschen"}>
                          <Trash2 size={18} />
                        </button>
                      )}
                    </div>

                    {/* ADMIN ONLY: FLUGSCHREIBER BUTTON */}
                    {isAdmin && (ticket.context || (ticket.system_log && ticket.system_log.length > 0)) && (
                      <button onClick={() => setExpandedLog(expandedLog === ticket.id ? null : ticket.id)} style={{ background: "transparent", border: `1px solid ${T.border2}`, color: T.textMuted, padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }} className="hov">
                        <Terminal size={12} /> {expandedLog === ticket.id ? "Logs ausblenden" : "Logs anzeigen"}
                      </button>
                    )}
                  </div>
                </div>

                {/* ADMIN ONLY: DER FLUGSCHREIBER-BEREICH */}
                {isAdmin && expandedLog === ticket.id && (
                  <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px dashed ${T.border2}`, animation: "fadeUp 0.2s ease" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#F59E0B", letterSpacing: "0.1em", display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
                      <Shield size={14} /> ADMIN DEBUG CONTEXT
                    </div>
                    
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
                      {ticket.context && Object.entries(ticket.context).map(([key, value]) => (
                        <div key={key} style={{ background: T.input, border: `1px solid ${T.border2}`, padding: "4px 8px", borderRadius: 6, fontSize: 11, color: T.textMuted }}>
                          <span style={{ color: T.textFaint }}>{key}:</span> {String(value)}
                        </div>
                      ))}
                    </div>

                    {ticket.system_log && ticket.system_log.length > 0 && (
                      <div style={{ background: "#0A0D15", borderRadius: 8, padding: "12px", border: "1px solid #1A2131", fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#A0ADC5", overflowX: "auto" }}>
                        {ticket.system_log.map((log, i) => (
                          <div key={i} style={{ marginBottom: 4 }}>
                            {log.includes("Error") ? <span style={{ color: "#FF4757" }}>{log}</span> : log}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}